import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { haptic } from '../../utils/haptics';

/**
 * ChoiceGroup — Auswahl aus wenigen, gleichrangigen Optionen (Segmented Control).
 *
 * Die Lücke, die das Audit im Session-Feedback aufgedeckt hat: Energielevel
 * (Niedrig/Mittel/Hoch) und die Drei-Wege-Zielauflösung (Geschafft/Später
 * weiter/Langzeitziel) waren je drei lose `<button>` nebeneinander. Optisch war
 * erkennbar, dass genau eines gewählt ist — für einen Screenreader waren es drei
 * unabhängige Aktionen, ohne Hinweis darauf, dass sie sich gegenseitig ausschliessen
 * oder wie viele es überhaupt sind.
 *
 * Dieses Primitive liefert das Muster, das die Plattform dafür vorsieht:
 * `role="radiogroup"` mit `role="radio"` + `aria-checked` je Option, plus
 * Roving Tabindex — die Gruppe ist EIN Tab-Stopp, innerhalb wird mit den
 * Pfeiltasten gewechselt. So verhalten sich native Radios auch.
 *
 * Farbe je Option ist möglich, aber die Ausnahme (TOKENS.md, Farbdiät): Sie muss
 * etwas bedeuten. Bei Energielevel tut sie das (wenig/mittel/viel), bei einer
 * Auswahl gleichwertiger Wege nicht — dort bleibt `tone` weg.
 */

// Feste Lookup-Map statt zusammengesetzter Klassennamen (Layout-Regel 2 in TOKENS.md:
// der Tailwind-JIT erzeugt `border-${x}/40` nicht).
// Haptik (Oktober 2026): `haptic.selection()` bei einem echten Wertwechsel,
// ausgelöst hier im Baustein; Aufrufer ergänzen keine. Optional verkettet,
// weil Tests `utils/haptics` oft nur teilweise mocken.
const tick = () => haptic?.selection?.();

const TONES = {
  neutral: 'border-border',
  success: 'border-success/40',
  warning: 'border-warning/40',
  danger: 'border-danger/40',
};

const ChoiceGroup = ({
  label,
  value,
  onChange,
  options,
  columns = 3,
  className = '',
}) => {
  const containerRef = useRef(null);

  // Pfeiltasten wandern durch die Optionen und wählen dabei direkt aus — so
  // verhält sich eine native Radiogruppe.
  const handleKeyDown = (event) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    const currentIndex = options.findIndex((o) => o.value === value);
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    // Bei noch leerer Auswahl beginnt die Reise am Anfang bzw. Ende.
    const from = currentIndex === -1 ? (step === 1 ? -1 : 0) : currentIndex;
    const nextIndex = (from + step + options.length) % options.length;

    if (options[nextIndex].value !== value) tick();
    onChange(options[nextIndex].value);
    containerRef.current?.querySelectorAll('[role="radio"]')[nextIndex]?.focus();
  };

  const gridClass = columns === 2 ? 'grid-cols-2' : columns === 4 ? 'grid-cols-4' : 'grid-cols-3';

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={`grid ${gridClass} gap-2 ${className}`}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            // Roving Tabindex: nur die gewählte Option (bzw. die erste, solange
            // nichts gewählt ist) liegt im Tab-Fluss.
            tabIndex={selected || (value == null && option === options[0]) ? 0 : -1}
            onClick={() => {
              if (option.value !== value) tick();
              onChange(option.value);
            }}
            className={[
              'flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border-2 px-2 py-2',
              'text-xs sm:text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              selected
                ? 'border-primary bg-primary-soft text-foreground'
                : `${TONES[option.tone] || TONES.neutral} bg-surface text-muted-foreground hover:bg-foreground/5`,
            ].join(' ')}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

ChoiceGroup.propTypes = {
  /** Beschriftung der Gruppe — wird der Accessible Name des Radiogroup */
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    /** nur setzen, wenn die Farbe etwas bedeutet (TOKENS.md, Farbdiät) */
    tone: PropTypes.oneOf(Object.keys(TONES)),
  })).isRequired,
  columns: PropTypes.oneOf([2, 3, 4]),
  className: PropTypes.string,
};

export default ChoiceGroup;
